<template>
  <v-flex class="video-room">
    <Loading v-if="loading" />
    <ErrorConnection v-if="error" />
    <AllowAccess v-if="!isPermissionsAccepted" />
    <v-flex class="video-room-container" ref="container" v-if="isPermissionsAccepted && !error">
      <div v-if="roomIsReady">
        <VisitFullScreenBtn v-if="!$vuetify.breakpoint.xsOnly" />
        <RemoteParticipant
          v-if="!loading"
          :participant="partnerParticipant"
          :title="partnerTitle"
          :initials="partnerInitials"
          :call-time="callTime"
          :muted="participantIsMute"
        />
        <LocalParticipant :class="isVideo ? '' : 'd-none'" v-if="localParticipant" :participant="localParticipant" />
      </div>
      <SettingsBtn
        v-if="$vuetify.breakpoint.xsOnly"
        @onChangeAudioOutput="onChangeAudioOutput"
        @onChangeAudioInput="onChangeAudioInput"
        @onChangeVideoInput="onChangeVideoInput"
      />
      <WaitingRoom v-if="!roomIsReady" />
      <VisitMediaControls @onToggleVideo="onToggleVideo" @onToggleAudio="onToggleAudio" />
    </v-flex>
    <VisitMediaControlsSelect
      v-if="!$vuetify.breakpoint.xsOnly && isPermissionsAccepted && !error"
      @onChangeAudioOutput="onChangeAudioOutput"
      @onChangeAudioInput="onChangeAudioInput"
      @onChangeVideoInput="onChangeVideoInput"
    />
  </v-flex>
</template>

<script>
import Video from "twilio-video";
import { createLocalAudioTrack, createLocalTracks, createLocalVideoTrack } from "twilio-video";
import { mapActions, mapGetters, mapMutations } from "vuex";

import { snackBarEventBus, snackBarEventName } from "../../eventBuses/snackBar.eventBus";
export default {
  components: {
    VisitMediaControls: () => import("./VisitMediaControls"),
    VisitFullScreenBtn: () => import("./FullScreenBtn"),
    SettingsBtn: () => import("./SettingsBtn"),
    VisitMediaControlsSelect: () => import("./VisitMediaControlsSelect.vue"),
    AllowAccess: () => import("./overlays/AllowAccess.vue"),
    Loading: () => import("./overlays/Loading.vue"),
    LocalParticipant: () => import("./LocalParticipant.vue"),
    RemoteParticipant: () => import("./RemoteParticipant/Index.vue"),
    ErrorConnection: () => import("./overlays/ErrorConnection.vue"),
    WaitingRoom: () => import("./WaitingRoom.vue"),
  },
  props: {
    callTime: String,
  },
  data() {
    return {
      participants: [],
      videoRoom: null,
      loading: false,
      localParticipant: null,
      error: null,
      participantIsMute: false,
    };
  },
  computed: {
    ...mapGetters("videoRoom", [
      "isVideo",
      "isAudio",
      "isPermissionsAccepted",
      "accessToken",
      "room",
      "settings",
      "roomIsReady",
      "encounter",
    ]),
    ...mapGetters(["role"]),
    partnerParticipant() {
      return this.participants[0];
    },
    videoSettings() {
      const deviceId = this.settings && this.settings.videoInput;
      return deviceId
        ? {
            deviceId: { exact: deviceId },
          }
        : null;
    },
    audioSettings() {
      const deviceId = this.settings && this.settings.audioInput;
      return deviceId
        ? {
            deviceId: { exact: deviceId },
          }
        : null;
    },
    partnerInitials() {
      return this.partnerFirstName[0].toUpperCase() + this.partnerLastName[0].toUpperCase();
    },
    partnerTitle() {
      const title = this.role === "patient" ? this.$t("general.titles.dr") : "";
      return `${title} ${this.partnerFirstName} ${this.partnerLastName}`;
    },
    partnerFirstName() {
      if (!this.encounter) {
        return "";
      }
      const partner = this.role === "patient" ? this.encounter.practitioner : this.encounter.patient;
      return partner.name.split(" ")[0];
    },
    partnerLastName() {
      if (!this.encounter) {
        return "";
      }
      const partner = this.role === "patient" ? this.encounter.practitioner : this.encounter.patient;
      return partner.name.split(" ")[1];
    },
  },
  methods: {
    ...mapMutations("videoRoom", ["setIsPermissionsAccepted"]),
    ...mapActions("videoRoom", ["getRoomAccessToken", "getRoomStatus"]),
    participantConnected(participant) {
      this.participants.push(participant);
    },
    participantDisconnected(participant) {
      this.participants = this.participants.filter((el) => el.sid !== participant.sid);
    },
    participantMuted(track) {
      if (track.kind !== "audio") return;
      this.participantIsMute = true;
    },
    participantUnmuted(track) {
      if (track.kind !== "audio") return;
      this.participantIsMute = false;
    },
    async onToggleVideo() {
      this.$gtag.event("click", { method: "Video toggle" });
      if (!this.localParticipant) {
        return;
      }
      if (this.isVideo) {
        await this.videoEnable();
      } else {
        await this.videoDisable();
      }
    },
    async onToggleAudio() {
      this.$gtag.event("click", { method: "Audio toggle" });
      if (!this.localParticipant) {
        return;
      }
      if (this.isAudio) {
        await this.audioInputPublish();
      } else {
        await this.audioInputUnPublish();
      }
    },
    async videoDisable() {
      await this.localParticipant.videoTracks.forEach((item) => item.track.disable());
    },
    async videoEnable() {
      await this.localParticipant.videoTracks.forEach((item) => item.track.enable());
    },
    async videoUnPublish() {
      for (const item of this.localParticipant.videoTracks.values()) {
        item.track.stop();
        await this.localParticipant.unpublishTrack(item.track);
      }
    },
    async videoPublish() {
      const track = await createLocalVideoTrack(this.videoSettings);
      await this.localParticipant.publishTrack(track);
    },
    async audioInputUnPublish() {
      for (const item of this.localParticipant.audioTracks.values()) {
        item.track.stop();
        await this.localParticipant.unpublishTrack(item.track);
      }
    },
    async audioInputPublish() {
      const track = await createLocalAudioTrack(this.audioSettings);
      await this.localParticipant.publishTrack(track);
    },
    async audioOutputSetup(room) {
      if (this.settings && this.settings.audioOutput) {
        room.on("trackSubscribed", (track) => {
          if (track.kind === "audio") {
            const audioElement = track.attach();
            audioElement.setSinkId(this.settings.audioOutput).then(() => document.body.appendChild(audioElement));
          }
        });
      }
    },
    async onChangeAudioInput() {
      if (!this.localParticipant) {
        return;
      }
      await this.audioInputUnPublish();
      if (this.isAudio) {
        await this.audioInputPublish();
      }
    },
    async onChangeAudioOutput() {
      if (!this.localParticipant) {
        return;
      }
      this.onConnect();
    },
    async onChangeVideoInput() {
      if (!this.localParticipant) {
        return;
      }
      await this.videoUnPublish();
      if (this.isVideo) {
        await this.videoPublish();
      }
    },
    async disconnect() {
      this.$gtag.event("click", { method: "VideoCall participant disconnect" });
      if (this.videoRoom) {
        await this.videoUnPublish();
        await this.audioInputUnPublish();
        this.videoRoom.localParticipant.tracks.forEach(function (track) {
          track.stop();
        });
        await this.videoRoom.disconnect();
        this.videoRoom = null;
        this.localParticipant = null;
        this.participants = [];
      }
    },
    async onConnect() {
      this.$gtag.event("click", { method: "VideoCall participant connect" });
      if (this.loading) {
        return false;
      }
      this.loading = true;
      try {
        this.error = null;
        await this.disconnect();
        const opt = {
          name: this.$store.getters.email,
          networkQuality: {
            local: 1,
            remote: 1,
          },
        };
        if (this.settings) {
          opt.tracks = await createLocalTracks({
            audio: this.settings.audioInput,
            video: this.settings.videoInput,
          });
        }
        const room = await Video.connect(this.accessToken, opt);
        this.localParticipant = room.localParticipant;
        await this.onToggleAudio();
        await this.onToggleVideo();
        room.participants.forEach((item) => this.participants.push(item));
        await this.audioOutputSetup(room);
        room.on("trackUnpublished", this.participantMuted);
        room.on("trackPublished", this.participantUnmuted);
        room.on("participantConnected", this.participantConnected);
        room.on("participantDisconnected", this.participantDisconnected);
        room.once("disconnected", (error) => room.participants.forEach(this.participantDisconnected));
        this.videoRoom = room;
      } catch (e) {
        this.error = e.message || e;
        snackBarEventBus.$emit(snackBarEventName, { message: this.error, type: "error" });
        await this.disconnect();
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    async roomIsReady() {
      if (this.room && this.roomIsReady && this.isPermissionsAccepted) {
        await this.getRoomAccessToken();
        await this.onConnect();
      }
    },
    async partnerParticipant() {
      await this.getRoomStatus();
    },
  },
  async mounted() {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      console.log(err);
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      this.setIsPermissionsAccepted(true);
      if (this.room && this.roomIsReady && this.isPermissionsAccepted) {
        await this.getRoomAccessToken();
        await this.onConnect();
      }
    } catch (err) {
      this.setIsPermissionsAccepted(false);
    }
  },
  async destroyed() {
    await this.disconnect();
  },
};
</script>

<style lang="scss">
.video-room {
  position: relative;
  background: black;
  border-radius: 5px;
  overflow: hidden;
  height: 100%;
  &-container {
    position: absolute;
    width: 100%;
    height: calc(100% - 62px);
    left: 0;
    right: 0;
    bottom: 62px;
    border-radius: 5px 5px 0 0;

    @media (max-width: 767.98px) {
      height: 100%;
      bottom: 0;
    }

    .participant {
      &-local {
        overflow: hidden;
        position: absolute;
        width: 20%;
        z-index: 2;
        bottom: 23px;
        right: 30px;
        padding: 0;
        video {
          background: #1e1e1e;
          position: relative;
          width: 100%;
          margin: 0;
          border-radius: 4px;
          border: 1px solid $white;
          transform: rotateY(180deg);
        }
      }
      &-remote {
        background: #1e1e1e;
        position: absolute;
        height: 100%;
        width: 100%;
        left: 0;
        right: 0;
        bottom: 0;
        &-component {
          background: #1e1e1e;
          position: absolute;
          height: 100%;
          width: 100%;
          left: 0;
          right: 0;
          video {
            height: 100%;
            width: 100%;
          }
        }
      }
      &-waiting {
        background: #1e1e1e;
        position: absolute;
        height: 100%;
        width: 100%;
        left: 0;
        right: 0;
        bottom: 0;
        video {
          height: 100%;
          width: 100%;
          opacity: 0.2;
          transform: rotateY(180deg);
        }
      }
    }
  }
}
</style>
